import type { BusinessSize, IndustryPlan } from '@/types';
import { simulate } from '@/services/api/client';
import { withBackend } from '@/services/api/backend';
import { PLANS as CATALOG, FREE_TRIAL_TOKENS } from '@/components/landing/pricingData';

/**
 * Synthetic "Free Trial" plan — a one-time grant of 50 tokens, never expires,
 * no payment required. Not a paid tier; used to seed the token-based free trial.
 */
export const freeTrialPlan: IndustryPlan = {
  id: 'plan_free_trial',
  name: 'Free Trial',
  description: '50 free tokens to explore Endeeko AI. No payment, no time limit.',
  tier: 'free',
  businessSize: 'Startup',
  priceMonthly: 0,
  priceYearly: 0,
  currency: 'USD',
  features: [
    { name: '50 AI tokens (one-time, never expires)', included: true },
    { name: 'Basic automation (job templates, simple trends)', included: true },
    { name: 'Industry browsing & assessment', included: true },
  ],
  tokenAllowance: FREE_TRIAL_TOKENS,
  seatLimit: 1,
  supportLevel: 'community',
  automationLimit: 'Add up to 2 automation features',
};

/**
 * In-app industry plans are derived from the canonical pricing catalog
 * (src/components/landing/pricingData.ts) so the plans here always match the
 * public pricing page exactly. Each plan's included features become `included`
 * rows; the feature matrix fills in the gaps across tiers.
 */
const PLANS: IndustryPlan[] = CATALOG.map((p) => ({
  id: p.id,
  name: p.name,
  description: p.description,
  tier: p.tier,
  businessSize: p.businessSize,
  priceMonthly: p.priceMonthly,
  priceYearly: p.priceYearly,
  currency: 'USD',
  freeTrialDays: p.freeTrialDays,
  recommended: p.highlighted,
  features: p.features.map((name) => ({ name, included: true })),
  tokenAllowance: p.monthlyTokens,
  seatLimit: p.seatLimit,
  supportLevel: p.supportLevel,
  overUsePrice: p.overusePrice,
  automationLimit: p.automationLimit,
}));

/** Guard against an empty backend result: plans must always render, so an empty
 *  or missing list falls back to the canonical catalog. */
const orCatalog = (list: IndustryPlan[] | null | undefined, fallback: IndustryPlan[] = PLANS) =>
  list && list.length ? list : fallback;

export const planService = {
  // Plan reads route through the endeeko `getIndustryPlans` callable; mock-first.
  // If the callable succeeds but returns no plans (e.g. plan data was wiped), we
  // still show the catalog so the details/checkout pages are never plan-less.
  list: async () => orCatalog(await withBackend<IndustryPlan[]>('getIndustryPlans', {}, () => simulate(PLANS))),
  listByIndustry: async (industryId: string) =>
    orCatalog(await withBackend<IndustryPlan[]>('getIndustryPlans', { industryId }, () => simulate(PLANS))),
  listByBusinessSize: async (size: BusinessSize) =>
    orCatalog(
      await withBackend<IndustryPlan[]>('getIndustryPlans', { businessSize: size }, () => simulate(PLANS.filter((p) => p.businessSize === size))),
      PLANS.filter((p) => p.businessSize === size),
    ),
  getById: (id: string) =>
    withBackend<IndustryPlan | null>('getPlan', { planId: id }, () => simulate(PLANS.find((p) => p.id === id) ?? null)),
  // Connected to endeeko `comparePlans` callable; mock-first via withBackend.
  compare: (ids: string[]) =>
    withBackend<IndustryPlan[]>('comparePlans', { planIds: ids }, () => simulate(PLANS.filter((p) => ids.includes(p.id)))),
};
